import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { FormValues } from '@features/users/adminPanel/components/Form/Form.types.ts';
import { QUERY_KEYS } from '@features/users/adminPanel/consts/queryKeys.ts';
import {
  getAdvertiserInsertionOrders,
  postAdvertiserInsertionOrder,
  putAdvertiserInsertionOrder,
} from '@features/users/adminPanel/services/advertiserInsertionOrders/service.ts';
import { transformUserProfilePayload } from '@features/users/adminPanel/services/userProfile/service.dto.ts';
import { SuccessMessage } from '@shared/components/Toast/SuccessMessage/SuccessMessage.tsx';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { renderErrorToastNotification } from '@shared/utils/renderErrorToastNotification.tsx';

interface UseAdvertisersProps {
  onSuccessCallback?: () => void;
  advertiserCollectionQueryEnabled: boolean;
}

export function useAdvertisers({ onSuccessCallback, advertiserCollectionQueryEnabled = false }: UseAdvertisersProps) {
  const { t } = useTranslation([i18nNameSpace.API]);
  const queryClient = useQueryClient();

  const refetchAdvertisersCollection = async () =>
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADVERTISER_INSERTION_ORDERS] });

  const {
    data,
    isPending: advertisersCollectionIsPending,
    isFetched: advertisersCollectionIsFetched,
  } = useQuery({
    queryKey: [QUERY_KEYS.ADVERTISER_INSERTION_ORDERS],
    queryFn: () => getAdvertiserInsertionOrders(),
    enabled: advertiserCollectionQueryEnabled,
  });

  const { mutate: postNewAdvertiserMutation, isPending: postNewAdvertiserMutationIsPending } = useMutation({
    mutationFn: (payload: FormValues) => postAdvertiserInsertionOrder(transformUserProfilePayload(payload)),
    onSuccess: async () => {
      await refetchAdvertisersCollection();
      toast.success(<SuccessMessage message={t('api.success.advertiserInsertionOrder.successfullyAdded')} />);
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
  });

  const { mutate: putAdvertiserMutation, isPending: putAdvertiserMutationIsPending } = useMutation({
    mutationFn: ({ payload, id }: { payload: FormValues; id: string }) =>
      putAdvertiserInsertionOrder(id, transformUserProfilePayload(payload)),
    onSuccess: async () => {
      await refetchAdvertisersCollection();
      toast.success(<SuccessMessage message={t('api.success.advertiserInsertionOrder.successfullyUpdated')} />);
    },
    onError: (error) => {
      renderErrorToastNotification(error);
    },
    onSettled: () => {
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
  });

  const advertisersCollection = data || [];

  return {
    advertisersCollection,
    advertisersCollectionIsPending,
    advertisersCollectionIsFetched,
    postNewAdvertiserMutation,
    postNewAdvertiserMutationIsPending,
    putAdvertiserMutation,
    putAdvertiserMutationIsPending,
  };
}
